/* eslint-disable react/prop-types */
import { Button, Dropdown, Input, Popover, Tag } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React from "react"
import { AiOutlineClockCircle } from "react-icons/ai"
import { FaMapMarker, FaRegCalendar } from "react-icons/fa"
import { IoMdInformationCircleOutline } from "react-icons/io"
import styled from "styled-components"

// ----------------------------------------------------------------------------

function _Slot(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const {
        className,
        slot,
        location,
        onChange,
        onDeleteBooking,
        onForceUnlock,
        agentReservationCodes,
        typeMap,
        mainEventType,
        availableTypes,
    } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function renderStatus() {
        function renderDefaultBadge() {
            if (slot.booker) {
                return <Tag color="orange">Prenotato</Tag>
            }
            if (slot.lockedStatus?.locked) {
                const { lockedUntil, lockedBy } = slot.lockedStatus
                return (
                    <Popover
                        content={`Bloccato da ${
                            lockedBy?.fiscalCode
                        } fino al ${dayjs(lockedUntil).format(
                            "D MMMM, HH:mm"
                        )}`}
                    >
                        <Tag color="orange">Bloccato</Tag>
                    </Popover>
                )
            }
            if (slot.visible) {
                return <Tag color="green">Abilitato</Tag>
            }

            return <Tag color="red">Disabilitato</Tag>
        }
        const reservationName = agentReservationCodes.find(
            ({ code }) => code === slot.agentReservation
        )?.label
        return (
            <div className="slot-heading-right">
                {renderDefaultBadge()}
                {slot.agentReservation && (
                    <Tag color="orange">
                        {reservationName || slot.agentReservation}
                    </Tag>
                )}
            </div>
        )
    }

    function renderActions() {
        if (!onChange) {
            return
        }
        const actions = []
        if (slot.lockedStatus?.locked) {
            actions.push(
                <Button
                    type="primary"
                    style={{
                        borderColor: "#d46b08",
                        backgroundColor: "#d46b08",
                    }}
                    onClick={onForceUnlock}
                >
                    Rimuovi blocco
                </Button>
            )
        } else if (!slot.booker) {
            if (slot.visible) {
                actions.push(
                    <Button
                        type="primary"
                        onClick={() => onChange("visible", false)}
                    >
                        Disabilita
                    </Button>
                )
            } else {
                actions.push(
                    <Button
                        type="primary"
                        onClick={() => onChange("visible", true)}
                    >
                        Abilita
                    </Button>
                )
            }
            if (slot.agentReservation) {
                actions.push(
                    <Button
                        type="default"
                        onClick={() => onChange("agentReservation", null)}
                    >
                        Non riservare
                    </Button>
                )
            } else {
                actions.push(
                    <Dropdown
                        trigger={"click"}
                        overlay={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0,
                                    background: "white",
                                    border: "1px solid rgba(0,0,0,0.3)",
                                }}
                            >
                                {agentReservationCodes.map(
                                    ({ code, label }) => {
                                        return (
                                            <Button
                                                type="text"
                                                key={code}
                                                onClick={() =>
                                                    onChange(
                                                        "agentReservation",
                                                        code
                                                    )
                                                }
                                            >
                                                {label}
                                            </Button>
                                        )
                                    }
                                )}
                            </div>
                        }
                    >
                        <Button type="default">Riserva ad agenti</Button>
                    </Dropdown>
                )
            }
        } else {
            actions.push(
                <Button type="primary" danger onClick={onDeleteBooking}>
                    Elimina prenotazione
                </Button>
            )
        }
        return <>{actions}</>
    }

    function renderTypeSelector() {
        if (!availableTypes?.length) {
            return
        }
        const mainType = typeMap[mainEventType]
        const slotType = typeMap[slot.eventType]
        const typeOptions = availableTypes
            ?.map((et) => typeMap[et])
            ?.filter((et) => et.id !== slot.eventType)
            .map((et) => (
                <Button
                    key={et.id}
                    type="text"
                    onClick={() => onChange("eventType", et.id)}
                >
                    {et.name}
                </Button>
            ))

        return (
            <Dropdown
                trigger={"click"}
                className="slot-type"
                overlay={
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            background: "white",
                            border: "1px solid rgba(0,0,0,0.3)",
                        }}
                    >
                        {typeOptions}
                    </div>
                }
            >
                <Button type="default">
                    {slotType?.name || mainType?.name}
                </Button>
            </Dropdown>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const isHybridSlot = slot.eventType && slot.eventType !== mainEventType

    return (
        <div className={`${className} ${isHybridSlot ? "hybrid" : ""}`}>
            <div className="slot-heading">
                <div className="slot-heading-left">
                    <div className="slot-date">
                        <FaRegCalendar /> {dayjs(slot.date).format("D MMM")}
                    </div>
                    <div className="slot-time">
                        <AiOutlineClockCircle /> {slot.window}
                    </div>
                </div>
                {renderStatus()}
            </div>
            <div className="slot-location">
                <FaMapMarker /> {location.address}, {location.city} (
                {location.province})
            </div>
            {onChange && (
                <div className="slot-notes">
                    <Input.TextArea
                        placeholder="Note"
                        value={slot.note}
                        onChange={(e) => {
                            onChange("note", e.target.value)
                        }}
                    ></Input.TextArea>
                </div>
            )}
            <div className="slot-actions">{renderActions()}</div>
            {renderTypeSelector()}
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_Slot.propTypes = {
    className: PropTypes.string.isRequired,
    location: PropTypes.object,
    slot: PropTypes.object,
    onChange: PropTypes.func,
    onDeleteBooking: PropTypes.func,
}

_Slot.defaultProps = {}

// ----------------------------------------------------------------------------

const Slot = styled(_Slot)`
    & {
        border: 2px solid ${(props) => props?.theme?.colors?.secondaryColor};
        border-radius: 16px;
        padding: 12px 6px;
        display: flex;
        flex-direction: column;
        gap: 15px;

        &.hybrid {
            border-color: orange;
        }

        svg {
            width: 18px;
            height: 18px;
        }

        .slot-heading {
            display: flex;
            justify-content: space-between;
            /* font-size: 16px; */
            font-weight: medium;
            align-items: flex-start;

            .slot-heading-left {
                display: flex;
                gap: 6px;
                justify-content: flex-start;
                flex-wrap: wrap;
            }

            .slot-heading-right {
                display: flex;
                gap: 0px;
            }

            .slot-date {
                display: flex;
                align-items: center;
                gap: 6px;
                white-space: nowrap;

                svg {
                    margin-bottom: 2px;
                }
            }

            .slot-time {
                display: flex;
                align-items: center;
                gap: 6px;
                white-space: nowrap;
            }
        }

        .slot-location {
            display: flex;
            align-items: center;
            gap: 6px;
        }
        .slot-actions {
            display: flex;
            flex-direction: row;
            gap: 6px;

            > * {
                flex: 1 0;
                display: block;
            }
        }
        .slot-type {
            margin-top: -6px;
        }
    }
`
// ----------------------------------------------------------------------------

export default Slot
